const io = require("socket.io-client");
const { SOCKET } = require("../utils/secure/secureConfig");

let socket = null;

function getAuth() {
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("loginDetails")
        : null;
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      const token =
        parsed?.token ||
        parsed?.data?.token ||
        parsed?.jwt ||
        parsed?._id ||
        raw;
      return token ? { token } : {};
    } catch (_e) {
      return raw ? { token: raw } : {};
    }
  } catch (_e) {
    return {};
  }
}

function connect() {
  if (socket && socket.connected) return socket;
  const url = SOCKET.baseUrl; // e.g., http://localhost:8083
  const namespace = SOCKET.namespace; // e.g., /secure-messaging
  const auth = getAuth();
  console.log("[SOCKET] Connecting to:", url + namespace);
  console.log(
    "[SOCKET] Auth token present:",
    !!auth.token,
    auth.token ? `(len=${String(auth.token).length})` : "(none)"
  );
  if (auth.token && typeof auth.token === "string") {
    const looksJson =
      auth.token.trim().startsWith("{") || auth.token.trim().startsWith("[");
    console.log(
      "[SOCKET] Auth token type:",
      looksJson ? "JSON-like (unexpected)" : "JWT/string"
    );
  }
  socket = io(url + namespace, {
    transports: ["websocket", "polling"],
    withCredentials: true,
    auth, // send only { token }
  });

  socket.on("connect", () => {
    console.log("[SOCKET] Connected to secure messaging namespace", {
      id: socket.id,
      nsp: socket.nsp,
    });
  });

  socket.on("disconnect", (reason) => {
    console.log("[SOCKET] Disconnected from secure messaging:", reason);
  });

  socket.on("connect_error", (error) => {
    console.error("[SOCKET] Connection error:", error);
  });

  socket.on("error", (error) => {
    console.error("[SOCKET] Socket error:", error);
  });

  return socket;
}

function disconnect() {
  if (socket) {
    try {
      socket.removeAllListeners();
    } catch (_e) {}
    socket.disconnect();
    socket = null;
  }
}

function ensure() {
  return socket && socket.connected ? socket : connect();
}

function joinSecureChat(chatId) {
  if (!chatId) {
    console.warn("🔐 [SOCKET] joinSecureChat: chatId is required");
    return;
  }
  console.log("🔐 [SOCKET] Joining secure chat:", chatId, {
    connected: !!ensure().connected,
    id: ensure().id,
  });
  ensure().emit("join secure chat", String(chatId), (ack) => {
    console.log("🔐 [SOCKET] Join ack:", ack);
  });
}

function emitSecureMessage(payload) {
  console.log("🔐 [SOCKET] Emitting secure message:", {
    chatId: payload?.chat?._id,
    hasContent: !!payload?.content,
    hasIv: !!payload?.iv,
    hasAuthTag: !!payload?.authTag,
    sender: payload?.sender,
  });
  ensure().emit("secure_message", payload);
}

function emitTyping(chatId, userId, isTyping) {
  console.log("🔐 [SOCKET] Emitting typing event:", {
    chatId,
    userId,
    isTyping: !!isTyping,
  });
  if (!userId) {
    console.warn("🔐 [SOCKET] emitTyping called without userId");
  }
  ensure().emit(
    "secure_typing",
    { chatId, userId, isTyping: !!isTyping },
    (ack) => {
      console.log("🔐 [SOCKET] Typing ack:", ack);
    }
  );
}

function emitMessagesRead(chatId, userId) {
  console.log("🔐 [SOCKET] Emitting messages read:", { chatId, userId });
  ensure().emit("secure_messages_read", { chatId, userId });
}

function emitMessageEdited(
  chatId,
  messageId,
  content,
  iv,
  authTag,
  editedAt,
  sender,
  isEdited = true
) {
  console.log("🔐 [SOCKET] Emitting message edited:", {
    chatId,
    messageId,
    hasContent: !!content,
    hasIv: !!iv,
    hasAuthTag: !!authTag,
    editedAt,
    sender: sender?._id || sender,
    isEdited,
  });
  ensure().emit("secure_message_edited", {
    chatId,
    messageId,
    content,
    iv,
    authTag,
    editedAt,
    sender,
    isEdited,
  });
}

function emitMessageDeleted(chatId, messageId, deletedAt, sender) {
  console.log("🔐 [SOCKET] Emitting message deleted:", {
    chatId,
    messageId,
    deletedAt,
    sender: sender?._id || sender,
  });
  ensure().emit("secure_message_deleted", {
    chatId,
    messageId,
    deletedAt,
    sender,
  });
}

function onSecureMessage(handler) {
  console.log("🔐 [SOCKET] Setting up secure message listener");
  const wrappedHandler = (data) => {
    console.log("🔐 [SOCKET] Received secure message:", {
      chatId: data?.chat?._id,
      hasContent: !!data?.content,
      hasIv: !!data?.iv,
      hasAuthTag: !!data?.authTag,
      sender: data?.sender,
    });
    handler(data);
  };
  ensure().on("secure_message", wrappedHandler);

  // Return unsubscribe function
  return () => {
    if (socket) {
      socket.off("secure_message", wrappedHandler);
    }
  };
}
function onTyping(handler) {
  console.log("🔐 [SOCKET] Setting up typing listener");
  const wrappedHandler = (data) => {
    console.log("🔐 [SOCKET] Received typing event:", data);
    handler(data);
  };
  ensure().on("secure_typing", wrappedHandler);

  // Return unsubscribe function
  return () => {
    if (socket) {
      socket.off("secure_typing", wrappedHandler);
    }
  };
}
function onMessagesRead(handler) {
  console.log("🔐 [SOCKET] Setting up messages read listener");
  const wrappedHandler = (data) => {
    console.log("🔐 [SOCKET] Received messages read event:", data);
    handler(data);
  };
  ensure().on("secure_messages_read", wrappedHandler);

  // Return unsubscribe function
  return () => {
    if (socket) {
      socket.off("secure_messages_read", wrappedHandler);
    }
  };
}

function onMessageEdited(handler) {
  console.log("🔐 [SOCKET] Setting up message edited listener");
  const wrappedHandler = (data) => {
    console.log("🔐 [SOCKET] Received message edited event:", data);
    handler(data);
  };
  ensure().on("secure_message_edited", wrappedHandler);

  // Return unsubscribe function
  return () => {
    if (socket) {
      socket.off("secure_message_edited", wrappedHandler);
    }
  };
}

function onMessageDeleted(handler) {
  console.log("🔐 [SOCKET] Setting up message deleted listener");
  const wrappedHandler = (data) => {
    console.log("🔐 [SOCKET] Received message deleted event:", data);
    handler(data);
  };
  ensure().on("secure_message_deleted", wrappedHandler);

  // Return unsubscribe function
  return () => {
    if (socket) {
      socket.off("secure_message_deleted", wrappedHandler);
    }
  };
}

function onSecureChatCreated(handler) {
  console.log("🔐 [SOCKET] Setting up chat created listener");
  const wrappedHandler = (data) => {
    const payload = Array.isArray(data) ? data[0] : data;
    try {
      console.log("🔐 [SOCKET] Received secure_chat_created:", {
        chatId: payload?._id,
        isGroupChat: payload?.isGroupChat,
        chatName: payload?.chatName,
        usersCount: Array.isArray(payload?.users) ? payload.users.length : 0,
        hasLatestMessage: !!payload?.latestMessage,
        latestMessage: payload?.latestMessage
          ? {
              _id: payload.latestMessage?._id,
              isDeleted: payload.latestMessage?.isDeleted,
              isEdited: payload.latestMessage?.isEdited,
              createdAt: payload.latestMessage?.createdAt,
              senderId:
                payload.latestMessage?.sender?._id ||
                payload.latestMessage?.sender,
            }
          : null,
        updatedAt: payload?.updatedAt,
      });
    } catch (e) {
      console.warn("🔐 [SOCKET] secure_chat_created log error:", e);
    }
    handler(payload);
  };
  ensure().on("secure_chat_created", wrappedHandler);
  return () => {
    if (socket) socket.off("secure_chat_created", wrappedHandler);
  };
}

function onSecureChatUpdated(handler) {
  console.log("🔐 [SOCKET] Setting up chat updated listener");
  const wrappedHandler = (data) => {
    const payload = Array.isArray(data) ? data[0] : data;
    try {
      console.log("🔐 [SOCKET] Received secure_chat_updated:", {
        chatId: payload?.chatId,
        hasLatestMessage: !!payload?.latestMessage,
        latestMessage: payload?.latestMessage
          ? {
              _id: payload.latestMessage?._id,
              isDeleted: payload.latestMessage?.isDeleted,
              isEdited: payload.latestMessage?.isEdited,
              createdAt: payload.latestMessage?.createdAt,
              senderId:
                payload.latestMessage?.sender?._id ||
                payload.latestMessage?.sender,
            }
          : null,
        updatedAt: payload?.updatedAt,
      });
    } catch (e) {
      console.warn("🔐 [SOCKET] secure_chat_updated log error:", e);
    }
    handler(payload);
  };
  ensure().on("secure_chat_updated", wrappedHandler);
  return () => {
    if (socket) socket.off("secure_chat_updated", wrappedHandler);
  };
}

function off(event, handler) {
  if (!socket) return;
  socket.off(event, handler);
}

module.exports = {
  connect,
  disconnect,
  joinSecureChat,
  emitSecureMessage,
  emitTyping,
  emitMessagesRead,
  emitMessageEdited,
  emitMessageDeleted,
  onSecureMessage,
  onTyping,
  onMessagesRead,
  onMessageEdited,
  onMessageDeleted,
  onSecureChatCreated,
  onSecureChatUpdated,
  off,
};
